import type { FormState, InputTextInputEventDetail, PIconProps } from "@porsche-design-system/components-react"
import { PInputText, PText } from "@porsche-design-system/components-react"
import debounce from "lodash-es/debounce"
import type { FocusEvent, KeyboardEvent } from "react"
import { forwardRef, useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useNumberFormattingUtils } from "./useNumberFormattingUtils"

export type InputNumberValue = number | undefined

export interface InputNumberProps {
	compact?: boolean
	debounced?: number
	disabled?: boolean
	errorMessage?: string
	hideLabel?: boolean
	icon?: PIconProps["name"]
	label?: string
	maxLength?: number
	minLength?: number
	name: string
	onChange?: (value: InputNumberValue, event: CustomEvent<InputTextInputEventDetail>) => void
	placeholder?: string
	readOnly?: boolean
	required?: boolean
	state?: FormState
	type: "number" | "currency"
	unit?: string
	unitPosition?: "prefix" | "suffix"
	value?: InputNumberValue
}

export const InputNumber = forwardRef<any, InputNumberProps>((props, ref) => {
	const {
		compact,
		debounced,
		disabled,
		errorMessage,
		hideLabel,
		label,
		maxLength,
		minLength,
		name,
		onChange,
		placeholder,
		readOnly,
		required,
		state,
		type,
		unit,
		unitPosition,
		value,
		...rest
	} = props

	const { decimalSeparator, formatNumber, parseNumber, resolvedUnit, resolvedUnitPosition, thousandSeparator } =
		useNumberFormattingUtils({ type, unit, unitPosition })

	const [hasPendingChanges, setHasPendingChanges] = useState(false)
	const [displayValue, setDisplayValue] = useState("")
	const [isFocused, setIsFocused] = useState(false)
	const hostRef = useRef<HTMLElement>(null)
	const caretPositionRef = useRef<number | null>(null)

	// --- Sync external value ---
	useLayoutEffect(() => {
		if (isFocused) {
			return
		}

		setDisplayValue(value === undefined ? "" : formatNumber(value))
	}, [value, formatNumber, isFocused])

	const onChangeDebounced = useMemo(
		() =>
			debounce((newValue: InputNumberValue, event: CustomEvent<InputTextInputEventDetail>) => {
				setHasPendingChanges(false)
				onChange?.(newValue, event)
			}, debounced),
		[onChange, debounced, setHasPendingChanges],
	)

	const handleChangeDebounced = useCallback(
		(newValue: InputNumberValue, event: CustomEvent<InputTextInputEventDetail>) => {
			setHasPendingChanges(true)
			onChangeDebounced(newValue, event)
		},
		[onChangeDebounced],
	)

	// --- Prevent invalid key presses ---
	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			// Allow all meta / ctrl shortcuts (copy, paste, select all, undo, redo)
			if (event.ctrlKey || event.metaKey) {
				return
			}

			const allowedNavigation = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"]
			if (allowedNavigation.includes(event.key)) {
				return
			}

			// Allow decimal separator (only once)
			if (type === "currency" && event.key === decimalSeparator) {
				if (displayValue.includes(decimalSeparator)) {
					event.preventDefault()
				}
				return
			}

			// Allow digits only
			if (!/^\d$/.test(event.key)) {
				event.preventDefault()
				return
			}

			// Prevent 3rd decimal digit
			if (type === "currency" && displayValue.includes(decimalSeparator)) {
				const [, decPart = ""] = displayValue.split(decimalSeparator)

				const input = event.currentTarget.shadowRoot!.querySelector("input")!
				const selectionStart = input.selectionStart ?? 0

				const decimalIndex = displayValue.indexOf(decimalSeparator)
				const isTypingInDecimalPart = selectionStart > decimalIndex

				if (isTypingInDecimalPart && decPart.length >= 2) {
					event.preventDefault()
				}
			}
		},
		[decimalSeparator, displayValue, type],
	)

	const handleInput = useCallback(
		(event: CustomEvent<InputTextInputEventDetail>) => {
			if (disabled ?? !onChange) {
				return
			}

			// Capture caret before state update for case when input is empty initially
			const input = (event.currentTarget as HTMLElement).shadowRoot?.querySelector("input")
			caretPositionRef.current = input?.selectionStart ?? null

			let rawValue = (event.target as HTMLInputElement).value

			// Remove everything except digits + decimal separator
			const allowedChars = new RegExp(`[^0-9${decimalSeparator}]`, "g")
			rawValue = rawValue.replace(allowedChars, "")

			// Number inputs are integer only, so pasted decimals are cut off
			if (type === "number") {
				rawValue = rawValue.split(decimalSeparator)[0]
			}

			// Only one decimal separator
			const parts = rawValue.split(decimalSeparator)
			if (parts.length > 2) {
				rawValue = parts[0] + decimalSeparator + parts.slice(1).join("")
			}

			// Prevent leading zeros like 0001
			if (rawValue.length > 1 && rawValue.startsWith("0") && !rawValue.startsWith(`0${decimalSeparator}`)) {
				rawValue = rawValue.replace(/^0+/, "")
				if (rawValue === "") {
					rawValue = "0"
				}
			}

			// Limit decimals to 2 in currency mode
			if (type === "currency" && rawValue.includes(decimalSeparator)) {
				const [intPart, decPart] = rawValue.split(decimalSeparator)
				rawValue = intPart + decimalSeparator + decPart.slice(0, 2)
			}

			setDisplayValue(rawValue)

			const parsedValue = parseNumber(rawValue)

			if (parsedValue === value) {
				return
			}

			const callback = debounced ? handleChangeDebounced : onChange
			return callback(parsedValue, event)
		},
		[debounced, decimalSeparator, disabled, handleChangeDebounced, onChange, parseNumber, type, value],
	)

	const handleFocus = useCallback(
		(event: FocusEvent<HTMLInputElement>) => {
			setIsFocused(true)

			if (value === undefined) {
				return
			}

			const host = event.currentTarget

			// Capture correct caret before updating displayValue, restore is happening via useLayoutEffect
			requestAnimationFrame(() => {
				// input might be unmounted already
				const input = host.shadowRoot?.querySelector("input")
				if (!input) {
					return
				}

				const formattedValue = input.value
				const caretPosition = input.selectionStart ?? 0

				// Count thousand separators BEFORE caret
				const separatorsBeforeCaret = formattedValue
					.slice(0, caretPosition)
					.split("")
					.filter((char) => char === thousandSeparator).length

				caretPositionRef.current = caretPosition - separatorsBeforeCaret

				const editable = value.toString().replace(".", decimalSeparator)
				setDisplayValue(editable)
			})
		},
		[decimalSeparator, thousandSeparator, value],
	)

	const handleBlur = useCallback(() => {
		setIsFocused(false)

		const parsed = parseNumber(displayValue)
		const formatted = formatNumber(parsed)

		setDisplayValue(formatted)
	}, [displayValue, formatNumber, parseNumber])

	// Restore caret position after displayValue was updated
	useLayoutEffect(() => {
		if (caretPositionRef.current == null) {
			return
		}
		if (!isFocused) {
			return
		}

		requestAnimationFrame(() => {
			const input = hostRef.current!.shadowRoot!.querySelector("input")!

			input.setSelectionRange(caretPositionRef.current, caretPositionRef.current)
			caretPositionRef.current = null
		})
	}, [displayValue, isFocused])

	return (
		<PInputText
			data-testid={`input-number_${name}`}
			{...rest}
			ref={(el) => {
				hostRef.current = el
				if (typeof ref === "function") {
					ref(el)
				} else if (ref) {
					ref.current = el
				}
			}}
			name={name}
			label={label}
			hideLabel={hideLabel}
			compact={compact}
			state={errorMessage && !hasPendingChanges ? "error" : state}
			message={errorMessage && !hasPendingChanges ? errorMessage : undefined}
			value={displayValue}
			required={required}
			placeholder={placeholder}
			maxLength={maxLength} // NOTE: decimalSeparator counts into this
			minLength={minLength}
			autoComplete="off"
			disabled={disabled}
			readOnly={readOnly}
			onKeyDown={handleKeyDown}
			onInput={handleInput}
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			{resolvedUnit && (
				<PText slot={resolvedUnitPosition === "suffix" ? "end" : "start"} color="contrast-medium" aria-hidden>
					{resolvedUnit}
				</PText>
			)}
		</PInputText>
	)
})

InputNumber.displayName = "InputNumber"
